import React, { useState, useEffect } from 'react';
import './EventForm.css';

const EventForm = ({ event, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    event_title: '',
    eventTitle: '',
    description: '',
    venue: '',
    venueName: '',
    'location/address': '',
    address: '',
    latitude: '',
    longitude: '',
    categoryPrimary: '',
    category: '',
    categorySecondary: '',
    subcategory: '',
    start_time: '',
    startTime: '',
    end_time: '',
    endTime: '',
    format: 'In-person',
    region: 'DC',
    coverArtUrl: '',
    isReservationsNeeded: false,
    typicalHours: '',
    isFlexible: true
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Categories and subcategories
  const categories = [
    'Professional', 'Arts/Culture', 'Social/Fun', 'Education', 'Wellness',
    'Iconic Landmarks', 'Food & Dining', 'Wellness & Lifestyle'
  ];

  const subcategories = {
    'Professional': ['Conference', 'Workshop', 'Networking', 'Panel', 'Briefing'],
    'Arts/Culture': ['Exhibition', 'Performance', 'Museum', 'Gallery', 'Theater'],
    'Social/Fun': ['Party', 'Meetup', 'Social', 'Entertainment', 'Nightlife'],
    'Education': ['Workshop', 'Class', 'Lecture', 'Seminar', 'Training'],
    'Wellness': ['Fitness', 'Yoga', 'Meditation', 'Spa', 'Health'],
    'Iconic Landmarks': ['Tours', 'Observatory', 'Cultural', 'Public Space', 'Market'],
    'Food & Dining': ['Restaurant', 'Cafe', 'Bar', 'Food Truck', 'Market'],
    'Wellness & Lifestyle': ['Spa', 'Tea', 'Wellness', 'Fitness', 'Relaxation']
  };

  const formats = ['In-person', 'Online', 'Hybrid'];
  const regions = ['DC', 'NYC'];

  useEffect(() => {
    if (event) {
      setFormData({
        event_title: event.event_title || event.eventTitle || '',
        eventTitle: event.eventTitle || event.event_title || '',
        description: event.description || '',
        venue: event.venue || event.venueName || '',
        venueName: event.venueName || event.venue || '',
        'location/address': event['location/address'] || event.address || '',
        address: event.address || event['location/address'] || '',
        latitude: event.latitude || '',
        longitude: event.longitude || '',
        categoryPrimary: event.categoryPrimary || event.category || '',
        category: event.category || event.categoryPrimary || '',
        categorySecondary: event.categorySecondary || event.subcategory || '',
        subcategory: event.subcategory || event.categorySecondary || '',
        start_time: event.start_time || event.startTime || '',
        startTime: event.startTime || event.start_time || '',
        end_time: event.end_time || event.endTime || '',
        endTime: event.endTime || event.end_time || '',
        format: event.format || 'In-person',
        region: event.region || 'DC',
        coverArtUrl: event.coverArtUrl || '',
        isReservationsNeeded: event.isReservationsNeeded || false,
        typicalHours: event.typicalHours || '',
        isFlexible: event.isFlexible !== undefined ? event.isFlexible : true
      });
    }
  }, [event]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.event_title && !formData.eventTitle) {
      newErrors.event_title = 'Event title is required';
    }
    if (!formData.description) {
      newErrors.description = 'Description is required';
    }
    if (!formData.venue && !formData.venueName) {
      newErrors.venue = 'Venue is required';
    }
    if (!formData['location/address'] && !formData.address) {
      newErrors['location/address'] = 'Address is required';
    }
    if (!formData.categoryPrimary && !formData.category) {
      newErrors.categoryPrimary = 'Category is required';
    }
    if (formData.latitude && (isNaN(formData.latitude) || formData.latitude < -90 || formData.latitude > 90)) {
      newErrors.latitude = 'Latitude must be between -90 and 90';
    }
    if (formData.longitude && (isNaN(formData.longitude) || formData.longitude < -180 || formData.longitude > 180)) {
      newErrors.longitude = 'Longitude must be between -180 and 180';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Clean up the data before saving
      const cleanedData = {
        ...formData,
        // Ensure both formats are consistent
        event_title: formData.event_title || formData.eventTitle,
        eventTitle: formData.eventTitle || formData.event_title,
        venue: formData.venue || formData.venueName,
        venueName: formData.venueName || formData.venue,
        'location/address': formData['location/address'] || formData.address,
        address: formData.address || formData['location/address'],
        categoryPrimary: formData.categoryPrimary || formData.category,
        category: formData.category || formData.categoryPrimary,
        categorySecondary: formData.categorySecondary || formData.subcategory,
        subcategory: formData.subcategory || formData.categorySecondary,
        start_time: formData.start_time || formData.startTime,
        startTime: formData.startTime || formData.start_time,
        end_time: formData.end_time || formData.endTime,
        endTime: formData.endTime || formData.end_time,
        // Convert coordinates to numbers
        latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
        longitude: formData.longitude ? parseFloat(formData.longitude) : undefined
      };

      onSave(cleanedData);
    } catch (error) {
      console.error('Error saving event:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="event-form-overlay">
      <div className="event-form-modal">
        <div className="form-header">
          <h2>{event ? '✏️ Edit Event' : '➕ Add New Event'}</h2>
          <button onClick={onCancel} className="close-btn">×</button>
        </div>

        <form onSubmit={handleSubmit} className="event-form">
          <div className="form-tabs">
            <div className="tab active">Basic Info</div>
            <div className="tab">Location</div>
            <div className="tab">Schedule</div>
            <div className="tab">Media</div>
          </div>

          <div className="form-content">
            {/* Basic Information */}
            <div className="form-section">
              <h3>Basic Information</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="event_title">Event Title *</label>
                  <input
                    type="text"
                    id="event_title"
                    name="event_title"
                    value={formData.event_title}
                    onChange={handleChange}
                    className={errors.event_title ? 'error' : ''}
                    placeholder="Enter event title"
                  />
                  {errors.event_title && <span className="error-text">{errors.event_title}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="description">Description *</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className={errors.description ? 'error' : ''}
                    placeholder="Enter event description"
                    rows="4"
                  />
                  {errors.description && <span className="error-text">{errors.description}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="venue">Venue *</label>
                  <input
                    type="text"
                    id="venue"
                    name="venue"
                    value={formData.venue}
                    onChange={handleChange}
                    className={errors.venue ? 'error' : ''}
                    placeholder="Enter venue name"
                  />
                  {errors.venue && <span className="error-text">{errors.venue}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="format">Format</label>
                  <select
                    id="format"
                    name="format"
                    value={formData.format}
                    onChange={handleChange}
                  >
                    {formats.map(format => (
                      <option key={format} value={format}>{format}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="categoryPrimary">Category *</label>
                  <select
                    id="categoryPrimary"
                    name="categoryPrimary"
                    value={formData.categoryPrimary}
                    onChange={handleChange}
                    className={errors.categoryPrimary ? 'error' : ''}
                  >
                    <option value="">Select category</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  {errors.categoryPrimary && <span className="error-text">{errors.categoryPrimary}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="categorySecondary">Subcategory</label>
                  <select
                    id="categorySecondary"
                    name="categorySecondary"
                    value={formData.categorySecondary}
                    onChange={handleChange}
                  >
                    <option value="">Select subcategory</option>
                    {formData.categoryPrimary && subcategories[formData.categoryPrimary]?.map(sub => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="region">Region</label>
                  <select
                    id="region"
                    name="region"
                    value={formData.region}
                    onChange={handleChange}
                  >
                    {regions.map(region => (
                      <option key={region} value={region}>{region}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="typicalHours">Typical Hours</label>
                  <input
                    type="text"
                    id="typicalHours"
                    name="typicalHours"
                    value={formData.typicalHours}
                    onChange={handleChange}
                    placeholder="e.g., 9 AM - 5 PM"
                  />
                </div>
              </div>
            </div>

            {/* Location Information */}
            <div className="form-section">
              <h3>Location</h3>
              
              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="location/address">Address *</label>
                  <input
                    type="text"
                    id="location/address"
                    name="location/address"
                    value={formData['location/address']}
                    onChange={handleChange}
                    className={errors['location/address'] ? 'error' : ''}
                    placeholder="Enter full address"
                  />
                  {errors['location/address'] && <span className="error-text">{errors['location/address']}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="latitude">Latitude</label>
                  <input
                    type="number"
                    id="latitude"
                    name="latitude"
                    value={formData.latitude}
                    onChange={handleChange}
                    className={errors.latitude ? 'error' : ''}
                    placeholder="e.g., 38.9072"
                    step="any"
                  />
                  {errors.latitude && <span className="error-text">{errors.latitude}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="longitude">Longitude</label>
                  <input
                    type="number"
                    id="longitude"
                    name="longitude"
                    value={formData.longitude}
                    onChange={handleChange}
                    className={errors.longitude ? 'error' : ''}
                    placeholder="e.g., -77.0369"
                    step="any"
                  />
                  {errors.longitude && <span className="error-text">{errors.longitude}</span>}
                </div>
              </div>
            </div>

            {/* Schedule */}
            <div className="form-section">
              <h3>Schedule</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="start_time">Start Time</label>
                  <input
                    type="text"
                    id="start_time"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleChange}
                    placeholder="e.g., Oct 15, 2025 • 2:00 PM"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="end_time">End Time</label>
                  <input
                    type="text"
                    id="end_time"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleChange}
                    placeholder="e.g., Oct 15, 2025 • 4:00 PM"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="isReservationsNeeded"
                      checked={formData.isReservationsNeeded}
                      onChange={handleChange}
                    />
                    Reservations Required
                  </label>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="isFlexible"
                      checked={formData.isFlexible}
                      onChange={handleChange}
                    />
                    Flexible Timing
                  </label>
                </div>
              </div>
            </div>

            {/* Media */}
            <div className="form-section">
              <h3>Media</h3>
              
              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="coverArtUrl">Cover Image URL</label>
                  <input
                    type="url"
                    id="coverArtUrl"
                    name="coverArtUrl"
                    value={formData.coverArtUrl}
                    onChange={handleChange}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn-cancel">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="btn-save">
              {isSubmitting ? 'Saving...' : (event ? 'Update Event' : 'Create Event')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventForm;
